import { Eye, Info, ListChecks, LogOut, Mail, Plus, RotateCcw, Shield, Sparkles, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { AIUsage, NutrientTracking, RecurringFood, SessionView, SettingsView } from '../../shared/api';
import { APP_NAME } from '../../shared/brand';
import { MEAL_LABELS, MEALS, monthDay, type ISODate, type Meal } from '../../shared/dates';
import { GROUP_LABELS, isBaseKey, scaleMap, type GoalDirection, type NutrientGroup } from '../../shared/nutrients';
import { ACTIVITY, GAIN_PACE_HINT, goalDirection, gramsFromPercents, macroPercents, missingBasics, paceCeilingKg, type Activity, type Sex } from '../../shared/targets';
import { MAX_TREND_WINDOW_DAYS, MIN_TREND_WINDOW_DAYS } from '../../shared/trend';
import { formatWeight, fromDisplayWeight, kitchenUnitsFor, toDisplayWeight, type KitchenUnits, type WeightUnit } from '../../shared/units';
import { ApiError } from '../api/client';
import {
  useCreateNutrient,
  useDeleteAccount,
  useDeleteNutrient,
  useIsAdmin,
  useRecurring,
  useResetAccount,
  useResetDemo,
  useSettings,
  useStartLinkEmail,
  useStopRecurring,
  useUnlinkEmail,
  useUpdateGoals,
  useUpdateProfile,
  useUpdateRecurring,
  useUpdateTracking,
  useSession,
  useSignOut,
  useVerifyLinkEmail,
} from '../api/hooks';
import { FatSecretAttribution } from '../components/FatSecretAttribution';
import { GramsInput } from '../components/GramsInput';
import { isReauthRequired, ReauthPrompt } from '../components/ReauthPrompt';
import { amount, directionWord, kcal, unitLabel } from '../lib/format';
import { parseDecimal } from '../../shared/numbers';
import { useAIAllowance } from '../lib/aiAllowance';
import { AI_OFF_FIX, useAIOffReason } from '../lib/aiOff';
import { applyTheme, readTheme, type ThemePref } from '../lib/theme';
import { useUI } from '../lib/ui';
import { forgetGoogleAutoSelect } from './SignInPage';
import { AIConsentControls } from '../components/AIConsent';
import { GeminiPrivacy } from '../components/GeminiPrivacy';
import { formatPaceInput, paceInputCaution, paceInputError, parsePaceInput } from '../lib/pace';
import { basicsInWords, heightText, parseBirthYear, parseHeight } from '../lib/profileFields';
import { paceOptions } from '../lib/setup';

const KITCHEN_CHOICES: ReadonlyArray<readonly [KitchenUnits, string]> = [
  ['us', 'Cups and spoons'],
  ['metric', 'Metric'],
];

function ProfileSection({ settings }: { settings: SettingsView }) {
  const { toast } = useUI();
  const update = useUpdateProfile();
  const p = settings.profile;
  const [unit, setUnit] = useState<WeightUnit>(p.weightUnit);
  // Until it's chosen, kitchen measures follow the weight unit, including one changed here before saving.
  const [kitchen, setKitchen] = useState<KitchenUnits | undefined>(p.kitchenUnits);
  const kitchenShown = kitchenUnitsFor({ kitchenUnits: kitchen, weightUnit: unit });
  // Sex, birth year and height can be unknown (skipped in setup): blank here, and saved as unknown if left blank.
  const [sex, setSex] = useState<Sex | null>(p.sex);
  const [birthYear, setBirthYear] = useState(p.birthYear === null ? '' : String(p.birthYear));
  const [height, setHeight] = useState(() => heightText(p.heightCm));
  // Shown in whole inches or centimetres; saved as stored unless it's typed, so other edits never round it.
  const [heightTyped, setHeightTyped] = useState(false);
  const typeHeight = (patch: Partial<typeof height>) => {
    setHeight((h) => ({ ...h, ...patch }));
    setHeightTyped(true);
  };
  const [activity, setActivity] = useState<Activity>(p.activity);
  const [goal, setGoal] = useState(p.goalKg !== null ? String(toDisplayWeight(p.goalKg, p.weightUnit)) : '');
  const [pace, setPace] = useState(p.paceKgPerWeek);
  const [trendWindow, setTrendWindow] = useState(String(p.trendWindowDays));
  const goalKgNow = (() => {
    const n = parseDecimal(goal);
    return n !== null && n > 0 ? fromDisplayWeight(n, unit) : null;
  })();
  // The same lose/gain split the wizard uses: presets within 1% when losing, ½ kg when gaining.
  const direction = goalDirection(settings.currentKg, goalKgNow);
  const presetDirection = direction === 'gain' ? 'gain' : 'lose';
  const presets = paceOptions(unit, presetDirection, settings.currentKg);
  const [paceMode, setPaceMode] = useState<'list' | 'custom'>(() => {
    const startDirection = goalDirection(settings.currentKg, p.goalKg);
    const startPresets = paceOptions(p.weightUnit, startDirection === 'gain' ? 'gain' : 'lose', settings.currentKg);
    return p.paceKgPerWeek > 0 && !startPresets.some((kg) => Math.abs(kg - p.paceKgPerWeek) < 1e-6) ? 'custom' : 'list';
  });
  const [paceCustom, setPaceCustom] = useState(() => (paceMode === 'custom' ? formatPaceInput(p.paceKgPerWeek, p.weightUnit) : ''));
  const customError = paceMode === 'custom' && paceCustom.trim() ? paceInputError(paceCustom, unit, direction) : null;
  const customNote = paceMode === 'custom' && !customError ? paceInputCaution(paceCustom, unit, direction) : null;
  const listOver = paceMode === 'list' && pace > paceCeilingKg(direction) + 1e-9;

  const save = () => {
    // A typo must never clear the goal: NaN serializes to null.
    const goalText = goal.trim();
    const goalNum = goalText === '' ? null : parseDecimal(goalText);
    if (goalText !== '' && (goalNum === null || goalNum <= 0)) {
      toast(`Enter your goal weight as a number, like ${unit === 'lb' ? '165' : '72.5'}`, { tone: 'error' });
      return;
    }
    const year = parseBirthYear(birthYear, new Date().getFullYear());
    if (year === 'invalid') {
      toast(`Enter your birth year, like 1988, or leave it blank`, { tone: 'error' });
      return;
    }
    const heightCm = heightTyped ? parseHeight({ unit, ...height }) : p.heightCm;
    if (heightCm === 'invalid') {
      toast(unit === 'lb' ? 'Enter a height from 3 ft 4 in to 8 ft 2 in, or leave it blank' : 'Enter a height from 100 to 250 cm, or leave it blank', { tone: 'error' });
      return;
    }
    const windowDays = Number(trendWindow.trim());
    if (!Number.isInteger(windowDays) || windowDays < MIN_TREND_WINDOW_DAYS || windowDays > MAX_TREND_WINDOW_DAYS) {
      toast(`Trend window should be a whole number from ${MIN_TREND_WINDOW_DAYS} to ${MAX_TREND_WINDOW_DAYS}`, { tone: 'error' });
      return;
    }
    let paceKg = pace;
    if (paceMode === 'custom') {
      const problem = paceInputError(paceCustom, unit, direction);
      if (problem) {
        toast(problem, { tone: 'error' });
        return;
      }
      const parsed = parsePaceInput(paceCustom, unit);
      if (typeof parsed !== 'number') return;
      paceKg = parsed;
    } else if (paceKg > paceCeilingKg(direction) + 1e-9) {
      toast(`Enter a pace up to ${formatWeight(paceCeilingKg(direction), unit)} a week.`, { tone: 'error' });
      return;
    }
    update.mutate(
      {
        weightUnit: unit,
        ...(kitchen ? { kitchenUnits: kitchen } : {}),
        sex,
        birthYear: year,
        heightCm,
        activity,
        goalKg: goalNum === null ? null : fromDisplayWeight(goalNum, unit),
        paceKgPerWeek: paceKg,
        trendWindowDays: windowDays,
      },
      { onSuccess: () => toast('Profile saved'), onError: (e) => toast(e.message, { tone: 'error' }) },
    );
  };

  return (
    <section className="card card--pad stack" style={{ gap: 16 }}>
      <h2 className="section-title">Profile &amp; goal</h2>
      <div className="grid-2">
        <label className="field">
          <span className="field__label">Sex (for energy estimates)</span>
          <select className="select" value={sex ?? ''} onChange={(e) => setSex(e.target.value === '' ? null : (e.target.value as Sex))}>
            <option value="">Not set</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other or prefer not to say</option>
          </select>
        </label>
        <label className="field">
          <span className="field__label">Birth year</span>
          <input className="input num" inputMode="numeric" placeholder="Not set" value={birthYear} onChange={(e) => setBirthYear(e.target.value)} />
        </label>
      </div>
      {/* Stacked on phones, so feet and inches have room to show their values. */}
      <div className="grid-2 grid-2--stack-narrow">
        <div className="field">
          <span className="field__label">Units</span>
          <div className="seg seg--block" role="radiogroup" aria-label="Weight unit">
            {(['lb', 'kg'] as const).map((u) => (
              <button
                key={u}
                role="radio"
                aria-checked={unit === u}
                className={`seg__btn ${unit === u ? 'is-active' : ''}`}
                onClick={() => {
                  const g = parseDecimal(goal);
                  if (g !== null && g > 0) setGoal(String(toDisplayWeight(fromDisplayWeight(g, unit), u)));
                  // The height's text is kept in every unit. A typed one is re-read so it converts too; a cleared one clears every unit.
                  if (heightTyped) {
                    const h = parseHeight({ unit, ...height });
                    if (h !== 'invalid') setHeight(heightText(h));
                  }
                  if (paceMode === 'custom') {
                    const typed = parsePaceInput(paceCustom, unit);
                    if (typeof typed === 'number') setPaceCustom(formatPaceInput(typed, u));
                  }
                  setUnit(u);
                }}
              >
                {u === 'lb' ? 'lb · ft' : 'kg · cm'}
              </button>
            ))}
          </div>
        </div>
        {unit === 'lb' ? (
          <div className="field">
            <span className="field__label">Height</span>
            <div className="height-row">
              <div className="input-affix input-affix--short">
                <input className="input num" inputMode="numeric" aria-label="Feet" placeholder="–" value={height.ft} onChange={(e) => typeHeight({ ft: e.target.value })} />
                <span className="input-affix__suffix">ft</span>
              </div>
              <div className="input-affix input-affix--short">
                <input className="input num" inputMode="decimal" aria-label="Inches" placeholder="–" value={height.inch} onChange={(e) => typeHeight({ inch: e.target.value })} />
                <span className="input-affix__suffix">in</span>
              </div>
            </div>
          </div>
        ) : (
          <label className="field">
            <span className="field__label">Height</span>
            <div className="input-affix input-affix--short">
              <input className="input num" inputMode="decimal" placeholder="Not set" value={height.cm} onChange={(e) => typeHeight({ cm: e.target.value })} />
              <span className="input-affix__suffix">cm</span>
            </div>
          </label>
        )}
      </div>
      <div className="field">
        <span className="field__label">Kitchen measures</span>
        <div className="seg seg--block" role="radiogroup" aria-label="Kitchen measures" style={{ maxWidth: 360 }}>
          {KITCHEN_CHOICES.map(([value, text]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={kitchenShown === value}
              className={`seg__btn ${kitchenShown === value ? 'is-active' : ''}`}
              onClick={() => setKitchen(value)}
            >
              {text}
            </button>
          ))}
        </div>
        <span className="field__hint">Shown beside amounts, like ≈&nbsp;1½&nbsp;cups or ≈&nbsp;350&nbsp;ml.</span>
      </div>
      <label className="field">
        <span className="field__label">Activity</span>
        <select className="select" value={activity} onChange={(e) => setActivity(e.target.value as Activity)}>
          {Object.entries(ACTIVITY).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label} · {v.hint}
            </option>
          ))}
        </select>
      </label>
      <div className="grid-2">
        <label className="field">
          <span className="field__label">Goal weight</span>
          <div className="input-affix">
            <input className="input num" inputMode="decimal" value={goal} placeholder="optional" onChange={(e) => setGoal(e.target.value)} />
            <span className="input-affix__suffix">{unit}</span>
          </div>
        </label>
        <div className="field">
          <span className="field__label">Pace</span>
          <select
            className="select"
            aria-label="Pace"
            value={paceMode === 'custom' ? 'custom' : String(pace)}
            onChange={(e) => {
              if (e.target.value === 'custom') {
                setPaceMode('custom');
                setPaceCustom((text) => text || (pace > 0 ? formatPaceInput(pace, unit) : ''));
                return;
              }
              setPaceMode('list');
              setPaceCustom('');
              setPace(Number(e.target.value));
            }}
          >
            <option value="0">Maintain</option>
            {presets.map((kg) => (
              <option key={kg} value={String(kg)}>
                {formatWeight(kg, unit, { decimals: unit === 'lb' ? 1 : 2 })} per week
              </option>
            ))}
            <option value="custom">Custom</option>
          </select>
          {paceMode === 'custom' && (
            <>
              <span className="input-affix">
                <input
                  className="input num"
                  inputMode="decimal"
                  aria-label={`Custom pace in ${unit} per week`}
                  aria-invalid={customError ? true : undefined}
                  placeholder={unit === 'lb' ? 'e.g. 1.25' : 'e.g. 0.4'}
                  value={paceCustom}
                  onChange={(e) => setPaceCustom(e.target.value)}
                />
                <span className="input-affix__suffix">{unit}</span>
              </span>
              {customError && <span className="field__error">{customError}</span>}
              {customNote && (
                <span className="pace-note" role="note">
                  {customNote}
                </span>
              )}
            </>
          )}
          {listOver && <span className="field__error">Enter a pace up to {formatWeight(paceCeilingKg(direction), unit)} a week.</span>}
          <span className="field__hint">
            {direction === 'gain' ? `${GAIN_PACE_HINT} Pick Custom for a pace of your own.` : 'Choices stay within 1% of your weight. Pick Custom for a pace of your own.'}
          </span>
        </div>
      </div>
      <label className="field">
        <span className="field__label">Trend window</span>
        <div className="input-affix">
          <input className="input num" inputMode="numeric" value={trendWindow} onChange={(e) => setTrendWindow(e.target.value)} />
          <span className="input-affix__suffix">days</span>
        </div>
        <span className="field__hint">Trailing average of daily weigh-ins. {MIN_TREND_WINDOW_DAYS}–{MAX_TREND_WINDOW_DAYS} days. A shorter window follows the scale faster.</span>
      </label>
      <button className="btn btn--primary" style={{ alignSelf: 'flex-start' }} disabled={update.isPending} onClick={save}>
        Save profile
      </button>
    </section>
  );
}

/** Split of calories for custom targets, from the current numbers, or a balanced split when there are none (counting without targets). */
function startingPercents(goals: SettingsView['goals']): { protein: number; carbs: number; fat: number } {
  if (goals.kcal === null || goals.protein === null || goals.carbs === null || goals.fat === null) return { protein: 30, carbs: 40, fat: 30 };
  return macroPercents({ kcal: goals.kcal, protein: goals.protein, carbs: goals.carbs, fat: goals.fat });
}

function TargetsSection({ settings }: { settings: SettingsView }) {
  const { toast } = useUI();
  const update = useUpdateGoals();
  const [mode, setMode] = useState(settings.goals.mode);
  const [kcalText, setKcal] = useState(settings.goals.kcal === null ? '' : String(settings.goals.kcal));
  const [pct, setPct] = useState(() => startingPercents(settings.goals));
  useEffect(() => {
    setMode(settings.goals.mode);
    setKcal(settings.goals.kcal === null ? '' : String(settings.goals.kcal));
    setPct(startingPercents(settings.goals));
  }, [settings.goals]);
  const kcalNum = parseDecimal(kcalText) ?? 0;
  const kcalValid = Number.isInteger(kcalNum) && kcalNum > 0;
  const grams = gramsFromPercents(kcalNum, pct);
  const total = pct.protein + pct.carbs + pct.fat;
  const s = settings.suggested;
  const missing = missingBasics(settings.profile, settings.currentKg);

  return (
    <section className="card card--pad stack" style={{ gap: 16 }}>
      <h2 className="section-title">Daily targets</h2>
      <div className="seg seg--block" role="radiogroup" aria-label="Target mode" style={{ maxWidth: 420 }}>
        {(['suggested', 'custom', 'off'] as const).map((m) => (
          <button key={m} role="radio" aria-checked={mode === m} className={`seg__btn ${mode === m ? 'is-active' : ''}`} onClick={() => setMode(m)}>
            {m === 'suggested' ? 'Suggested' : m === 'custom' ? 'Custom' : 'No goal'}
          </button>
        ))}
      </div>

      {mode === 'off' ? (
        <p className="small muted">No calorie or macro targets. {APP_NAME} counts everything you log, and Today shows what you ate without a budget.</p>
      ) : mode === 'suggested' ? (
        s ? (
          <div className="stack">
            <div className="grid-3">
              {[
                ['Calories', `${s.kcal.toLocaleString('en-US')}`, 'kcal'],
                ['Protein', `${s.protein}`, 'g'],
                ['Carbs · Fat', `${s.carbs} · ${s.fat}`, 'g'],
              ].map(([label, value, u]) => (
                <div key={label} className="card card--soft" style={{ padding: 12 }}>
                  <div className="stat__label">{label}</div>
                  <div className="stat__value num" style={{ fontSize: 22 }}>
                    {value} <span style={{ fontSize: 13 }}>{u}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="banner banner--ai">
              <Info size={17} />
              <ul className="small" style={{ margin: 0, paddingLeft: 16 }}>
                {s.explanation.map((line) => (
                  <li key={line}>{line}</li>
                ))}
                <li>Suggested targets update automatically as your trend weight changes.</li>
              </ul>
            </div>
          </div>
        ) : (
          <p className="small muted">
            {missing.length === 1 && missing[0] === 'weight'
              ? 'Log a weigh-in to get suggested targets.'
              : `Suggested targets need ${basicsInWords(missing)}. Add them in Profile above${missing.includes('weight') ? ' and weigh in' : ''}. Until then, ${APP_NAME} counts without targets.`}
          </p>
        )
      ) : (
        <div className="stack">
          <label className="field" style={{ maxWidth: 220 }}>
            <span className="field__label">Calories</span>
            <div className="input-affix">
              <input className="input num" inputMode="numeric" value={kcalText} aria-invalid={!kcalValid} onChange={(e) => setKcal(e.target.value)} />
              <span className="input-affix__suffix">kcal</span>
            </div>
          </label>
          <div className="grid-3">
            {(['protein', 'carbs', 'fat'] as const).map((k) => (
              <label key={k} className="field">
                <span className="field__label" style={{ textTransform: 'capitalize' }}>
                  {k} · {grams[k]} g
                </span>
                <div className="input-affix">
                  <input
                    className="input input--sm num"
                    inputMode="numeric"
                    value={pct[k]}
                    onChange={(e) => setPct((p) => ({ ...p, [k]: Math.max(0, Math.min(100, Number(e.target.value) || 0)) }))}
                  />
                  <span className="input-affix__suffix">%</span>
                </div>
              </label>
            ))}
          </div>
          {!kcalValid && <p className="field__error">Enter calories as a whole number, like 1800.</p>}
          {total !== 100 && <p className="field__error">Percentages add up to {total}% — make them 100%.</p>}
        </div>
      )}
      <button
        className="btn btn--primary"
        style={{ alignSelf: 'flex-start' }}
        disabled={update.isPending || (mode === 'custom' && (total !== 100 || !kcalValid))}
        onClick={() =>
          update.mutate(mode !== 'custom' ? { mode } : { mode, kcal: kcalNum, protein: grams.protein, carbs: grams.carbs, fat: grams.fat }, {
            onSuccess: () => toast('Targets saved'),
            onError: (e) => toast(e.message, { tone: 'error' }),
          })
        }
      >
        Save targets
      </button>
    </section>
  );
}

function NutrientsSection({ settings }: { settings: SettingsView }) {
  const { toast } = useUI();
  const saveTracking = useUpdateTracking();
  const create = useCreateNutrient();
  const remove = useDeleteNutrient();
  const [rows, setRows] = useState<Map<string, NutrientTracking>>(() => new Map(settings.tracking.map((t) => [t.key, t])));
  const [filter, setFilter] = useState('');
  const [newName, setNewName] = useState('');
  const [newUnit, setNewUnit] = useState('mg');
  const [newDir, setNewDir] = useState<GoalDirection>('max');
  const [newTarget, setNewTarget] = useState('');
  // What's typed in each target box, so "0." and "1,5" survive while typing.
  const [targetText, setTargetText] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    setRows(new Map(settings.tracking.map((t) => [t.key, t])));
    setTargetText(new Map());
  }, [settings.tracking]);
  const invalidTargets = [...targetText].filter(([key, text]) => {
    if (!rows.get(key)?.tracked || !text.trim()) return false;
    const n = parseDecimal(text);
    return n === null || n < 0;
  });

  const dirty = useMemo(
    () =>
      settings.tracking.filter((t) => {
        const r = rows.get(t.key);
        return r && (r.tracked !== t.tracked || r.target !== t.target || r.direction !== t.direction);
      }),
    [rows, settings.tracking],
  );
  const set = (key: string, patch: Partial<NutrientTracking>) =>
    setRows((prev) => {
      const next = new Map(prev);
      const cur = next.get(key);
      if (cur) next.set(key, { ...cur, ...patch });
      return next;
    });

  const groups = Object.keys(GROUP_LABELS) as NutrientGroup[];
  const q = filter.trim().toLowerCase();

  return (
    <section className="card card--pad stack" style={{ gap: 14 }}>
      <div>
        <h2 className="section-title">Nutrients</h2>
        <p className="small muted" style={{ marginTop: 4 }}>
          Calories, protein, carbs and fat are always tracked. Turn on anything else — or add your own — and it shows up on Today, in AI estimates and in
          Coach.
        </p>
      </div>
      <input className="input input--sm" type="search" placeholder="Find a nutrient…" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Find a nutrient" />
      <div>
        {groups.map((group) => {
          const defs = settings.nutrients.filter((n) => n.group === group && !isBaseKey(n.key) && (!q || n.name.toLowerCase().includes(q)));
          if (defs.length === 0) return null;
          return (
            <div className="ngroup" key={group}>
              <div className="ngroup__title">{GROUP_LABELS[group]}</div>
              {defs.map((def) => {
                const row = rows.get(def.key);
                if (!row) return null;
                return (
                  <div className="nrow" key={def.key}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{def.name}</div>
                      <div className="tiny muted">
                        {unitLabel(def)}
                        {def.dv ? ` · daily value ${amount(def.dv, def)} ${unitLabel(def)}` : ''}
                      </div>
                    </div>
                    <div className="row">
                      {def.custom && (
                        <button
                          className="btn btn--quiet btn--icon btn--xs"
                          aria-label={`Delete ${def.name}`}
                          onClick={() => remove.mutate(def.key, { onSuccess: () => toast(`Removed ${def.name}`), onError: (e) => toast(e.message, { tone: 'error' }) })}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      <button
                        className="switch"
                        role="switch"
                        aria-checked={row.tracked}
                        aria-label={`Track ${def.name}`}
                        onClick={() => set(def.key, { tracked: !row.tracked, target: row.target ?? def.dv ?? null })}
                      />
                    </div>
                    {row.tracked && (
                      <div className="nrow__edit">
                        <select className="select input--sm" value={row.direction} aria-label={`${def.name} goal type`} onChange={(e) => set(def.key, { direction: e.target.value as GoalDirection })}>
                          {(['min', 'max', 'target'] as const).map((d) => (
                            <option key={d} value={d}>
                              {directionWord(d)}
                            </option>
                          ))}
                        </select>
                        <div className="input-affix">
                          <input
                            className="input input--sm num"
                            inputMode="decimal"
                            aria-label={`${def.name} target`}
                            placeholder="no target"
                            value={targetText.get(def.key) ?? (row.target === null ? '' : String(row.target))}
                            aria-invalid={invalidTargets.some(([k]) => k === def.key)}
                            onChange={(e) => {
                              const text = e.target.value;
                              setTargetText((prev) => new Map(prev).set(def.key, text));
                              const n = parseDecimal(text);
                              if (!text.trim()) set(def.key, { target: null });
                              else if (n !== null && n >= 0) set(def.key, { target: n });
                            }}
                          />
                          <span className="input-affix__suffix">{unitLabel(def)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {invalidTargets.length > 0 && <p className="field__error">Targets must be numbers, like 25 or 2.5.</p>}
      {dirty.length > 0 && (
        <div className="row" style={{ position: 'sticky', bottom: 'calc(var(--tabbar-h) + 12px)', zIndex: 2 }}>
          <button
            className="btn btn--primary"
            disabled={saveTracking.isPending || invalidTargets.length > 0}
            onClick={() =>
              saveTracking.mutate(
                { items: dirty.map((t) => rows.get(t.key)!).map(({ key, tracked, target, direction }) => ({ key, tracked, target, direction })) },
                { onSuccess: () => toast('Nutrients saved'), onError: (e) => toast(e.message, { tone: 'error' }) },
              )
            }
          >
            Save {dirty.length} {dirty.length === 1 ? 'change' : 'changes'}
          </button>
          <button
            className="btn btn--ghost"
            onClick={() => {
              setRows(new Map(settings.tracking.map((t) => [t.key, t])));
              setTargetText(new Map());
            }}
          >
            Cancel
          </button>
        </div>
      )}
      <form
        className="card card--soft stack"
        style={{ padding: 14, gap: 10 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!newName.trim()) return;
          const target = newTarget.trim() === '' ? null : parseDecimal(newTarget);
          if (newTarget.trim() !== '' && (target === null || target < 0)) {
            toast('Targets must be numbers, like 25 or 2.5.', { tone: 'error' });
            return;
          }
          create.mutate(
            { name: newName.trim(), unit: newUnit, direction: newDir, target },
            {
              onSuccess: () => {
                toast(`Now tracking ${newName.trim()}`);
                setNewName('');
                setNewTarget('');
              },
              onError: (err) => toast(err.message, { tone: 'error' }),
            },
          );
        }}
      >
        <div style={{ fontWeight: 650 }}>Add your own nutrient</div>
        <div className="row wrap">
          <input className="input input--sm grow" style={{ minWidth: 160 }} placeholder="e.g. Water, Omega-6, Lutein" value={newName} onChange={(e) => setNewName(e.target.value)} aria-label="Nutrient name" />
          <select className="select input--sm" style={{ width: 'auto' }} value={newUnit} onChange={(e) => setNewUnit(e.target.value)} aria-label="Unit">
            {['g', 'mg', 'µg', 'IU', 'ml', 'unit'].map((u) => (
              <option key={u}>{u}</option>
            ))}
          </select>
          <select className="select input--sm" style={{ width: 'auto' }} value={newDir} onChange={(e) => setNewDir(e.target.value as GoalDirection)} aria-label="Goal type">
            {(['min', 'max', 'target'] as const).map((d) => (
              <option key={d} value={d}>
                {directionWord(d)}
              </option>
            ))}
          </select>
          <input className="input input--sm num" style={{ width: 110 }} inputMode="decimal" placeholder="target" value={newTarget} onChange={(e) => setNewTarget(e.target.value)} aria-label="Target" />
          <button className="btn btn--primary btn--sm" disabled={!newName.trim() || create.isPending}>
            <Plus size={15} /> Add
          </button>
        </div>
        <p className="tiny muted">Values come from foods you create, nutrition labels you scan, and AI estimates.</p>
      </form>
    </section>
  );
}

/** Foods the server adds to the log every day. Changes apply to days not added yet, so from tomorrow. */
function DailyFoodsSection() {
  const { today, toast } = useUI();
  const recurring = useRecurring();
  const update = useUpdateRecurring();
  const stop = useStopRecurring();
  const [editing, setEditing] = useState<{ id: string; grams: number; meal: Meal } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const baseId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  // Each row's Change and Stop buttons, so closing its editor or prompt can give focus back.
  const changeButtons = useRef(new Map<string, HTMLButtonElement>());
  const stopButtons = useRef(new Map<string, HTMLButtonElement>());
  const editingId = editing?.id ?? null;
  const foods = recurring.data ?? [];
  // Its row is gone (stopped): keep keyboard focus in this section.
  const focusTitle = () => requestAnimationFrame(() => titleRef.current?.focus());

  // The Change button goes away while its editor is open: move focus into the editor.
  useEffect(() => {
    if (editingId) editorRef.current?.focus({ preventScroll: true });
  }, [editingId]);

  const closeEditor = (id: string) => {
    setEditing(null);
    requestAnimationFrame(() => changeButtons.current.get(id)?.focus());
  };
  const since = (date: ISODate) => (date.slice(0, 4) === today.slice(0, 4) ? monthDay(date) : `${monthDay(date)}, ${date.slice(0, 4)}`);

  const save = (food: RecurringFood) => {
    if (!editing) return;
    update.mutate(
      { id: food.id, patch: { grams: editing.grams, meal: editing.meal } },
      {
        onSuccess: () => {
          toast(`Saved. ${food.name} changes from tomorrow.`);
          closeEditor(food.id);
        },
        onError: (e) => {
          // Stopped on another device: its row is about to go, so close the editor too.
          if (e instanceof ApiError && e.status === 404) {
            setEditing(null);
            focusTitle();
          }
          toast(e.message, { tone: 'error' });
        },
      },
    );
  };

  const cancelStop = (id: string) => {
    setConfirming(null);
    requestAnimationFrame(() => stopButtons.current.get(id)?.focus());
  };

  const stopFood = (food: RecurringFood) => {
    const stopped = () => {
      setConfirming(null);
      toast(`Stopped repeating ${food.name}`);
      focusTitle();
    };
    stop.mutate(food.id, {
      onSuccess: stopped,
      // Already stopped (say on another device) is the same outcome.
      onError: (e) => (e instanceof ApiError && e.status === 404 ? stopped() : toast(e.message, { tone: 'error' })),
    });
  };

  return (
    <section className="card card--pad stack" style={{ gap: 12 }}>
      <div>
        <h2 ref={titleRef} tabIndex={-1} className="section-title">
          Daily foods
        </h2>
        <p className="small muted" style={{ marginTop: 4 }}>
          Added to your log every day. Changes apply from tomorrow.
        </p>
      </div>
      {recurring.isPending ? (
        <div className="skeleton" style={{ height: 48 }} />
      ) : recurring.isError ? (
        <p className="field__error" role="alert">
          {recurring.error.message}
        </p>
      ) : foods.length === 0 ? (
        <p className="small muted">
          None yet. To add one, open a food in your log and choose <b>Repeat every day</b>.
        </p>
      ) : (
        <div className="list">
          {foods.map((food) => {
            const open = editing?.id === food.id;
            const asking = confirming === food.id && !open;
            const promptId = `${baseId}-${food.id}`;
            const busy = (update.isPending && update.variables?.id === food.id) || (stop.isPending && stop.variables === food.id);
            return (
              <div key={food.id} className="list-item wrap" style={{ gap: 8 }}>
                <div className="grow" style={{ flexBasis: 180 }}>
                  <div className="ellipsis" style={{ fontWeight: 600 }}>
                    {food.name}
                  </div>
                  <div className="tiny muted num">
                    {Math.round(food.grams)} g · {kcal(scaleMap(food.per100, food.grams).kcal)} kcal · {MEAL_LABELS[food.meal]} · since {since(food.startDate)}
                  </div>
                  {asking && (
                    <div id={promptId} className="tiny muted">
                      Stop adding it every day? What's already in your log stays.
                    </div>
                  )}
                </div>
                {asking && (
                  <div className="row" role="group" aria-label={`Stop repeating ${food.name}?`}>
                    <button className="btn btn--danger btn--sm" disabled={busy} aria-describedby={promptId} onClick={() => stopFood(food)}>
                      Yes, stop
                    </button>
                    {/* Focus lands on the safe choice; stopping is a deliberate second step. */}
                    <button className="btn btn--ghost btn--sm" autoFocus aria-describedby={promptId} onClick={() => cancelStop(food.id)}>
                      Cancel
                    </button>
                  </div>
                )}
                {!open && !asking && (
                  <div className="row">
                    <button
                      ref={(el) => {
                        if (el) changeButtons.current.set(food.id, el);
                        else changeButtons.current.delete(food.id);
                      }}
                      className="btn btn--quiet btn--sm"
                      aria-label={`Change ${food.name}`}
                      disabled={busy}
                      onClick={() => setEditing({ id: food.id, grams: food.grams, meal: food.meal })}
                    >
                      Change
                    </button>
                    <button
                      ref={(el) => {
                        if (el) stopButtons.current.set(food.id, el);
                        else stopButtons.current.delete(food.id);
                      }}
                      className="btn btn--quiet btn--sm"
                      aria-label={`Stop repeating ${food.name}`}
                      disabled={busy}
                      onClick={() => setConfirming(food.id)}
                    >
                      Stop
                    </button>
                  </div>
                )}
                {open && editing && (
                  <div ref={editorRef} tabIndex={-1} role="group" aria-label={`Change ${food.name}`} className="stack" style={{ flexBasis: '100%', gap: 10 }}>
                    <GramsInput
                      value={editing.grams}
                      onChange={(grams) => setEditing((e) => e && { ...e, grams })}
                      servings={food.servings}
                      kcalPer100={food.per100.kcal}
                      foodRef={food.foodRef}
                    />
                    {/* A select, not the meal buttons: four of them don't fit a list row on a phone. */}
                    <label className="field" style={{ maxWidth: 220 }}>
                      <span className="field__label">Meal</span>
                      <select
                        className="select input--sm"
                        value={editing.meal}
                        onChange={(e) => {
                          const meal = e.target.value as Meal;
                          setEditing((cur) => cur && { ...cur, meal });
                        }}
                      >
                        {MEALS.map((m) => (
                          <option key={m} value={m}>
                            {MEAL_LABELS[m]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="row wrap">
                      <button
                        className="btn btn--primary btn--sm"
                        disabled={busy || (editing.grams === food.grams && editing.meal === food.meal)}
                        onClick={() => save(food)}
                      >
                        Save
                      </button>
                      <button className="btn btn--ghost btn--sm" onClick={() => closeEditor(food.id)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Calories (and each size's, when changing one) come from FatSecret for its foods. */}
      {foods.some((f) => f.foodRef?.startsWith('fs:')) && <FatSecretAttribution />}
    </section>
  );
}

/**
 * What uses requests, as the code charges them (server/ai): one per model call. A Coach
 * message makes a call per round, up to five (maxToolRounds 4 plus the answer). Voice the
 * browser can't transcribe itself (Brave and Firefox, or after its speech service fails)
 * is a transcription plus the parse. Opening Progress writes a new insight on a new day or
 * after a weigh-in, and a new check-in each week; Refresh is an action of its own. A rare
 * retry of an unreadable answer also counts, hence "most".
 */
const WHAT_COUNTS =
  "Most AI actions use one request. A Coach message can use up to five, voice uses two when the browser can't transcribe it (as in Brave and Firefox), and opening Progress uses one when it writes a new weight insight or weekly check-in.";

function AIUsageLine({ usage: { used, limit } }: { usage: AIUsage }) {
  const allowance = useAIAllowance();
  return (
    <div className="stack" style={{ gap: 6 }}>
      {limit === null ? (
        <p className="small">
          You've used <b className="num">{used}</b> AI {used === 1 ? 'request' : 'requests'} today, no personal cap.
        </p>
      ) : (
        <>
          <p className="small">
            You've used <b className="num">{used}</b> of <b className="num">{limit}</b> AI {limit === 1 ? 'request' : 'requests'} today.{' '}
            <span className="muted">They reset {allowance.resetPhrase}.</span>
          </p>
          <div className="bar" style={{ maxWidth: 360 }} aria-hidden="true">
            <div className="bar__fill" style={{ width: `${limit > 0 ? Math.min(100, (used / limit) * 100) : 100}%`, background: used >= limit ? 'var(--bad)' : 'var(--ai)' }} />
          </div>
        </>
      )}
      {allowance.state === 'busy' && <p className="small">{allowance.message}</p>}
      <p className="small muted">{WHAT_COUNTS}</p>
    </div>
  );
}

/** Add a sign-in address: it gets a 6-digit code, the same way signing in by email works. */
function AddEmail() {
  const { toast } = useUI();
  const start = useStartLinkEmail();
  const verify = useVerifyLinkEmail();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<{ message: string; reauth: boolean } | null>(null);
  const [wait, setWait] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (wait <= 0) return;
    const id = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(id);
  }, [wait]);

  const close = () => {
    setOpen(false);
    setEmail('');
    setCode('');
    setSentTo(null);
    setError(null);
    // The form is gone; keep keyboard focus here rather than back at the top of the page.
    requestAnimationFrame(() => addRef.current?.focus());
  };
  // Detach a request still in flight, so its answer can't reopen the form at a stale step.
  const cancel = () => {
    start.reset();
    verify.reset();
    close();
  };

  const fail = (err: Error) => setError({ message: err.message, reauth: isReauthRequired(err) });
  const problem = error?.reauth ? (
    <ReauthPrompt message={error.message} />
  ) : error ? (
    <p className="field__error" role="alert">
      {error.message}
    </p>
  ) : null;

  const send = (to: string) => {
    setError(null);
    start.mutate(to, {
      // The answer is the same whether or not the address can be added, so always go on to the code.
      onSuccess: () => {
        setSentTo(to);
        setCode('');
        setWait(60);
        // A first send mounts the code box (autoFocus); a resend refocuses the one that's there.
        window.setTimeout(() => codeRef.current?.focus(), 0);
      },
      onError: fail,
    });
  };

  const submitCode = (to: string) => {
    setError(null);
    verify.mutate(
      { email: to, code },
      {
        onSuccess: () => {
          toast(`Added ${to}`);
          close();
        },
        onError: fail,
      },
    );
  };

  if (!open) {
    return (
      <button ref={addRef} className="btn btn--ghost btn--sm" style={{ alignSelf: 'flex-start' }} onClick={() => setOpen(true)}>
        <Plus size={15} /> Add an email
      </button>
    );
  }

  if (!sentTo) {
    return (
      <form
        className="card card--soft stack"
        style={{ padding: 14, gap: 10 }}
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) send(email.trim());
        }}
      >
        <label className="field">
          <span className="field__label">Email to add</span>
          <input
            className="input input--sm"
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <div className="row wrap">
          <button className="btn btn--primary btn--sm" disabled={start.isPending || !email.trim()}>
            <Mail size={15} /> Send a code
          </button>
          <button type="button" className="btn btn--quiet btn--sm" onClick={cancel}>
            Cancel
          </button>
        </div>
        {problem}
      </form>
    );
  }

  return (
    <form
      className="card card--soft stack"
      style={{ padding: 14, gap: 10 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (/^\d{6}$/.test(code)) submitCode(sentTo);
      }}
    >
      <p className="small muted">
        If <b>{sentTo}</b> can be added, a 6-digit code is on its way. It expires in 10 minutes.
      </p>
      <label className="field">
        <span className="field__label">Code</span>
        <input
          ref={codeRef}
          className="input num signin__code"
          style={{ maxWidth: 240 }}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        />
      </label>
      <div className="row wrap">
        <button className="btn btn--primary btn--sm" disabled={verify.isPending || code.length !== 6}>
          Verify
        </button>
        <button type="button" className="btn btn--quiet btn--sm" onClick={cancel}>
          Cancel
        </button>
      </div>
      {problem}
      <div className="row between wrap">
        <button
          type="button"
          className="btn btn--quiet btn--sm"
          onClick={() => {
            setSentTo(null);
            setError(null);
          }}
        >
          Use a different email
        </button>
        <button type="button" className="btn btn--quiet btn--sm" disabled={start.isPending || wait > 0} onClick={() => send(sentTo)}>
          {wait > 0 ? `Send a new code (${wait}s)` : 'Send a new code'}
        </button>
      </div>
    </form>
  );
}

/** The addresses that sign in to this account. */
function SignInEmails({ session }: { session: SessionView }) {
  const { toast } = useUI();
  const unlink = useUnlinkEmail();
  const baseId = useId();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [reauth, setReauth] = useState<string | null>(null);
  // Each row's Remove button, so a cancelled confirm can give focus back to it.
  const removeButtons = useRef(new Map<string, HTMLButtonElement>());
  const emails = session.user?.emails ?? [];
  const onlyOne = emails.length <= 1;
  const cancel = (email: string) => {
    setConfirming(null);
    requestAnimationFrame(() => removeButtons.current.get(email)?.focus());
  };

  return (
    <div className="stack" style={{ gap: 4 }}>
      <div className="ngroup__title">Sign-in emails</div>
      <div className="list">
        {emails.map((email, i) => {
          const current = email === session.email;
          const asking = confirming === email;
          const promptId = `${baseId}-${i}`;
          return (
            <div key={email} className="list-item wrap" style={{ gap: 8 }}>
              <div className="grow" style={{ flexBasis: 180 }}>
                <div className="ellipsis" style={{ fontWeight: 600 }}>
                  {email}
                </div>
                {asking ? (
                  <div id={promptId} className="tiny muted">
                    {current ? 'You signed in with this one, so removing it signs you out.' : 'Remove this email from your account?'}
                  </div>
                ) : (
                  current && <div className="tiny muted">Signed in with this one</div>
                )}
              </div>
              {asking ? (
                <div className="row" role="group" aria-label={`Remove ${email}?`}>
                  <button
                    className="btn btn--danger btn--sm"
                    disabled={unlink.isPending}
                    aria-describedby={promptId}
                    onClick={() => {
                      setReauth(null);
                      unlink.mutate(email, {
                        onSuccess: () => {
                          setConfirming(null);
                          toast(`Removed ${email}`);
                        },
                        onError: (err) => {
                          if (!isReauthRequired(err)) return toast(err.message, { tone: 'error' });
                          setConfirming(null);
                          setReauth(err.message);
                        },
                      });
                    }}
                  >
                    Yes, remove
                  </button>
                  {/* Focus lands on the safe choice; the destructive one is a deliberate second step. */}
                  <button className="btn btn--ghost btn--sm" autoFocus aria-describedby={promptId} onClick={() => cancel(email)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  ref={(el) => {
                    if (el) removeButtons.current.set(email, el);
                    else removeButtons.current.delete(email);
                  }}
                  className="btn btn--quiet btn--sm"
                  disabled={onlyOne}
                  aria-label={`Remove ${email}`}
                  onClick={() => setConfirming(email)}
                >
                  Remove
                </button>
              )}
            </div>
          );
        })}
      </div>
      {onlyOne && emails.length > 0 && (
        <p className="tiny muted">{session.emailCodes ? 'To remove this email, add another one first.' : "Your only sign-in email can't be removed."}</p>
      )}
      {reauth && <ReauthPrompt message={reauth} />}
      {session.emailCodes ? (
        <AddEmail />
      ) : (
        <p className="tiny muted">Adding another email needs emailed codes, which aren't set up on this server.</p>
      )}
    </div>
  );
}

/**
 * Setup again: see every step with your answers (nothing saved), run it again (your log, weigh-ins
 * and recipes stay), or erase everything and start setup from scratch.
 */
function SetupSection({ settings, signInOn }: { settings: SettingsView; signInOn: boolean }) {
  const { toast } = useUI();
  const navigate = useNavigate();
  const resetAccount = useResetAccount();
  const [eraseText, setEraseText] = useState('');
  // Erasing needs a recent sign-in when sign-in is on.
  const [reauth, setReauth] = useState<string | null>(null);
  return (
    <section className="card card--pad stack" style={{ gap: 14 }}>
      <div>
        <h2 className="section-title">Setup</h2>
        <p className="small muted" style={{ marginTop: 4 }}>
          The questions from when you joined: about you, your weight, how active you are, your goal and what you track.
        </p>
      </div>
      <div className="setup-actions">
        <div className="stack" style={{ gap: 6 }}>
          <button className="btn btn--ghost" style={{ alignSelf: 'flex-start' }} onClick={() => navigate('/setup/preview')}>
            <Eye size={16} aria-hidden="true" /> Preview setup
          </button>
          <p className="tiny muted">Every step, with your answers filled in. Nothing is saved.</p>
        </div>
        <div className="stack" style={{ gap: 6 }}>
          <button className="btn btn--ghost" style={{ alignSelf: 'flex-start' }} onClick={() => navigate('/setup')}>
            <ListChecks size={16} aria-hidden="true" /> Run setup again
          </button>
          <p className="tiny muted">Change your answers. Your food log, weigh-ins and recipes stay.</p>
        </div>
      </div>
      {settings.account.mode === 'personal' && (
        <div className="stack setup-erase" style={{ gap: 8 }}>
          <h3 className="setup-erase__title">Erase everything</h3>
          <p className="small muted">
            Deletes your food log, weigh-ins, recipes and settings, then starts setup from the beginning.
            {signInOn ? ' Your account and sign-in emails stay.' : ''}
          </p>
          <div className="row wrap">
            <input
              className="input input--sm"
              style={{ maxWidth: 220 }}
              placeholder='Type "erase" to confirm'
              aria-label='Type "erase" to confirm starting over'
              value={eraseText}
              onChange={(e) => setEraseText(e.target.value)}
            />
            <button
              className="btn btn--danger btn--sm"
              disabled={eraseText.trim().toLowerCase() !== 'erase' || resetAccount.isPending}
              onClick={() => {
                setReauth(null);
                resetAccount.mutate(undefined, { onError: (err) => (isReauthRequired(err) ? setReauth(err.message) : toast(err.message, { tone: 'error' })) });
              }}
            >
              <RotateCcw size={15} /> Erase everything and start over
            </button>
          </div>
          {reauth && <ReauthPrompt message={reauth} />}
        </div>
      )}
    </section>
  );
}

export function YouPage() {
  const { today, toast } = useUI();
  // Fresh on every visit: the AI usage line changes with each AI request.
  const settings = useSettings({ fresh: true });
  const reset = useResetDemo();
  const [theme, setTheme] = useState<ThemePref>(readTheme);
  const [confirmReset, setConfirmReset] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  // Deleting needs a recent sign-in when sign-in is on.
  const [dataReauth, setDataReauth] = useState<string | null>(null);
  const onDataError = (err: Error) => (isReauthRequired(err) ? setDataReauth(err.message) : toast(err.message, { tone: 'error' }));
  const session = useSession();
  const signOut = useSignOut();
  const deleteAccount = useDeleteAccount();
  const isAdmin = useIsAdmin();
  const aiOffReason = useAIOffReason();
  const signInOn = session.data?.required ?? false;
  const data = settings.data;

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">You</h1>
          <p className="page__sub">Goals, nutrients, and how the app works for you.</p>
        </div>
        {/* Wide screens have Admin in the sidebar. */}
        <div className="row wrap">
          <Link to="/about" className="btn btn--ghost btn--sm">
            <Info size={16} /> About
          </Link>
          {isAdmin && (
            <Link to="/admin" className="btn btn--ghost btn--sm hide-wide">
              <Shield size={16} /> Admin
            </Link>
          )}
        </div>
      </div>
      {!data ? (
        <div className="skeleton" style={{ height: 320, borderRadius: 20 }} />
      ) : (
        <>
          <ProfileSection key={`p-${data.profile.weightUnit}-${data.profile.goalKg}`} settings={data} />
          <TargetsSection settings={data} />
          <NutrientsSection settings={data} />
          <DailyFoodsSection />

          <section className="card card--pad stack" style={{ gap: 12 }}>
            <h2 className="section-title">Appearance</h2>
            <div className="seg" role="radiogroup" aria-label="Theme" style={{ alignSelf: 'flex-start' }}>
              {(['system', 'light', 'dark'] as const).map((t) => (
                <button
                  key={t}
                  role="radio"
                  aria-checked={theme === t}
                  className={`seg__btn ${theme === t ? 'is-active' : ''}`}
                  onClick={() => {
                    setTheme(t);
                    applyTheme(t);
                  }}
                >
                  {t[0]!.toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </section>

          <section className="card card--pad stack" style={{ gap: 12 }}>
            <h2 className="section-title">Food data</h2>
            <div className="row wrap">
              <span className={`badge ${data.foods.source === 'fatsecret' ? 'badge--good' : ''}`}>{data.foods.source === 'fatsecret' ? 'FatSecret' : 'Built-in catalog'}</span>
              <span className="small muted">
                Barcodes via <b>{data.foods.barcode === 'fatsecret' ? 'FatSecret' : 'Open Food Facts'}</b>
              </span>
            </div>
            <p className="small muted">
              {data.foods.source === 'fatsecret'
                ? 'Search and AI matching use FatSecret; nutrition is converted to per-100 g so grams stay the base unit. Your log stays in this app.'
                : 'Using the built-in catalog of ~220 common foods. Add FATSECRET_CLIENT_ID and FATSECRET_CLIENT_SECRET to .env to search FatSecret.'}
            </p>
          </section>

          <section className="card card--pad stack" style={{ gap: 12 }}>
            <h2 className="section-title">AI</h2>
            <div className="row wrap">
              <span className={`badge ${data.ai.enabled ? 'badge--good' : 'badge--warn'}`}>{data.ai.enabled ? 'On' : 'Off'}</span>
              <span className="small muted">
                Provider <b>{data.ai.provider}</b> · fast model <code>{data.ai.model}</code> · Coach model <code>{data.ai.smartModel}</code>
              </span>
            </div>
            {data.ai.enabled ? (
              <AIUsageLine usage={data.aiUsage} />
            ) : aiOffReason === 'consent' ? (
              <p className="small">AI is off for your account until you agree to what's sent to Gemini, below.</p>
            ) : (
              <p className="small muted">{AI_OFF_FIX[aiOffReason]}</p>
            )}
            {data.ai.provider === 'stub' && (
              <div className="banner banner--warn">
                <Sparkles size={17} />
                <span className="small">
                  <b>Demo mode.</b> AI answers are simulated offline from your data and the built-in food list.
                  {!signInOn && (
                    <>
                      {' '}
                      Set <code>AI_PROVIDER=gemini</code> and <code>GEMINI_API_KEY</code> in .env for real AI.
                    </>
                  )}
                </span>
              </div>
            )}
            <div className="banner banner--ai">
              <Sparkles size={17} />
              <GeminiPrivacy paidTier={data.ai.paidTier} />
            </div>
            {/* With sign-in on, each person agrees before AI features use their data, and can stop. */}
            {signInOn && (data.ai.offReason === null || data.ai.offReason === 'consent') && (
              <AIConsentControls consentAt={data.profile.aiConsentAt ?? null} needsConsent={data.ai.offReason === 'consent'} />
            )}
          </section>

          {session.data?.required && (
            <section className="card card--pad stack" style={{ gap: 16 }}>
              <div className="row between wrap" style={{ gap: 12 }}>
                <div>
                  <h2 className="section-title">Account</h2>
                  <p className="small muted">Signed in as {session.data.email}</p>
                </div>
                <div className="row wrap">
                  <button
                    className="btn btn--quiet btn--sm"
                    disabled={signOut.isPending}
                    title="Ends every session for this account, on all devices"
                    onClick={() => {
                      forgetGoogleAutoSelect();
                      signOut.mutate({ everywhere: true }, { onError: (e) => toast(e.message, { tone: 'error' }) });
                    }}
                  >
                    Sign out everywhere
                  </button>
                  <button
                    className="btn btn--ghost"
                    disabled={signOut.isPending}
                    onClick={() => {
                      forgetGoogleAutoSelect();
                      signOut.mutate({}, { onError: (e) => toast(e.message, { tone: 'error' }) });
                    }}
                  >
                    <LogOut size={16} /> Sign out
                  </button>
                </div>
              </div>
              <SignInEmails session={session.data} />
            </section>
          )}

          <SetupSection settings={data} signInOn={signInOn} />

          <section className="card card--pad stack" style={{ gap: 12 }}>
            <h2 className="section-title">Your data</h2>
            {data.account.mode === 'demo' ? (
              <>
                <p className="small muted">This is the synthetic demo account. Reset regenerates ten weeks of sample weigh-ins and meals.</p>
                {confirmReset ? (
                  <div className="row wrap">
                    <button
                      className="btn btn--danger"
                      disabled={reset.isPending}
                      onClick={() =>
                        reset.mutate(today, {
                          onSuccess: () => {
                            toast('Demo data reset');
                            setConfirmReset(false);
                          },
                          onError: (e) => toast(e.message, { tone: 'error' }),
                        })
                      }
                    >
                      Yes, erase and reseed
                    </button>
                    <button className="btn btn--ghost" onClick={() => setConfirmReset(false)}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button className="btn btn--ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setConfirmReset(true)}>
                    <RotateCcw size={16} /> Reset demo data
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="small muted">
                  {signInOn ? (
                    'Your log is stored on this server under your account. The person who runs it sees your name, emails, last visit and AI use, and can technically read anything stored here. Other members see nothing of yours but recipes you share by link, and the copies they add stay theirs.'
                  ) : (
                    <>
                      Your log lives on this computer in <code>data/grams.db</code> (git-ignored). Copy that file to back it up.
                    </>
                  )}
                </p>
              </>
            )}
            {signInOn && isAdmin && (
              <p className="small muted" style={{ marginTop: 8 }}>
                Admin accounts come from <code>ADMIN_EMAILS</code> in the server config, so this one can't be deleted here. Take your address out of
                it and redeploy first.
              </p>
            )}
            {signInOn && !isAdmin && (
              <div className="stack" style={{ gap: 10, marginTop: 8 }}>
                <p className="small muted">
                  Deletes your account and everything in it (your log, weigh-ins, foods, recipes, Coach chats and sign-in emails) and signs you out. You can't
                  undo this. Server backups keep a copy for up to 7 days.
                </p>
                <div className="row wrap">
                  <input
                    className="input input--sm"
                    style={{ maxWidth: 220 }}
                    placeholder='Type "delete" to confirm'
                    aria-label='Type "delete" to confirm deleting your account'
                    value={deleteText}
                    onChange={(e) => setDeleteText(e.target.value)}
                  />
                  <button
                    className="btn btn--danger btn--sm"
                    disabled={deleteText.trim().toLowerCase() !== 'delete' || deleteAccount.isPending}
                    onClick={() => {
                      setDataReauth(null);
                      forgetGoogleAutoSelect();
                      deleteAccount.mutate(undefined, { onSuccess: () => toast('Your account was deleted'), onError: onDataError });
                    }}
                  >
                    <Trash2 size={15} /> Delete my account
                  </button>
                </div>
              </div>
            )}
            {dataReauth && <ReauthPrompt message={dataReauth} />}
          </section>
        </>
      )}
    </div>
  );
}
